import { UserService } from '../../services/user.service';
import { UserAdminContractFormService } from '../../services/user-admin-contract-form.service';
import { LanguageService } from 'src/app/shared/services/language.service';

import { DatexPipe } from '../../../shared/datex.pipe';
import { IsNotEmptyObjectPipe } from '../../../shared/pipes/is-not-empty-object.pipe';
import { IsNilPipe } from 'src/app/pipes/is-nil.pipe';

import { CommonModule } from '@angular/common';
import { Component, effect, inject, input, output } from '@angular/core';
import { ReactiveFormsModule, UntypedFormArray } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle,
} from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';

import { TranslateModule } from '@ngx-translate/core';

import { FaIconComponent } from '@fortawesome/angular-fontawesome';

import { USER_ADMIN_GMA } from '../../constants/user-admin-contract.constants';
import { Moment } from 'moment';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';

export const MODULES = [
  CommonModule,
  MatCardModule,
  MatListModule,
  MatButtonModule,
  ReactiveFormsModule,
  MatFormFieldModule,
  MatInputModule,
  MatSelectModule,
  TranslateModule,
];
export const COMPONENTS = [FaIconComponent];
export const PIPES = [DatexPipe, IsNotEmptyObjectPipe, IsNilPipe];

@Component({
  selector: 'app-user-admin-contract-tab',
  templateUrl: './user-admin-contract-tab.component.html',
  styleUrls: ['./user-admin-contract-tab.component.scss'],
  imports: [
    ...MODULES,
    ...COMPONENTS,
    ...PIPES,
    MatRadioButton,
    MatRadioGroup,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelHeader,
    MatExpansionPanelTitle,
  ],
})
export class UserAdminContractTabComponent {
  public readonly languageService = inject(LanguageService);
  public readonly userService = inject(UserService);
  private readonly contractFormService = inject(UserAdminContractFormService);
  private readonly sanitizer = inject(DomSanitizer);

  readonly campbellWinterSenseSolar = USER_ADMIN_GMA.campbellWinterSenseSolar;
  readonly gmaManufacturerOptions = [
    {
      manufacturer: 'Lufft',
      models: ['IRS31Pro + WS', 'NIRS31 + WS'],
    },
    {
      manufacturer: 'Teconer',
      models: ['RWS10', 'RWS11', 'Mobiler Sensor'],
    },
    {
      manufacturer: 'Campbell',
      models: ['Wintersense Solar'],
    },
  ];

  contractStartDtg = input<Moment>();
  contractExpiryDtg = input<Moment>();
  contingents = input<any>();
  showContactEditInfo = input<boolean>();
  customerContactLines = input<string[]>();
  customerContact = input<string>();
  showRegionEditInfo = input<boolean>();
  customerInfoLines = input<string[]>();
  customerInfo = input<string>();

  // Outputs
  saveContactInfo = output<void>();
  editRegion = output<void>();
  saveRegionInfo = output<{ base64: string; name: string } | null>();
  showRegionEdit = output<boolean>();
  showContactEdit = output<boolean>();

  get contactRows(): UntypedFormArray {
    return this.contractFormService.contactRows;
  }

  isMapsLinkInRow(row: any): boolean {
    return this.getMapsUrlFromRow(row) !== null;
  }

  getSafeMapsLinkInRow(row: any): SafeUrl | null {
    const mapsUrl = this.getMapsUrlFromRow(row);

    if (!mapsUrl) {
      return null;
    }

    return this.sanitizer.bypassSecurityTrustUrl(mapsUrl);
  }

  private getMapsUrlFromRow(row: any): string | null {
    const value = row.get('gmaCoordinates')?.value || '';
    const urlMatch = value.match(/https?:\/\/[^\s]+/i);

    if (!urlMatch) {
      return null;
    }

    const rawUrl = urlMatch[0];

    try {
      const url = new URL(rawUrl);
      const hostname = url.hostname.toLowerCase();

      const isAllowedGoogleMapsHost =
        hostname.includes('google.') || hostname === 'maps.app.goo.gl';

      const isAllowedProtocol = url.protocol === 'https:';

      if (!isAllowedProtocol || !isAllowedGoogleMapsHost) {
        return null;
      }

      return url.toString();
    } catch {
      return null;
    }
  }

  get gmaRows(): UntypedFormArray {
    return this.contractFormService.gmaRows;
  }

  addGmaRow(): void {
    this.contractFormService.addGmaRow();
  }

  removeGmaRow(index: number): void {
    this.contractFormService.removeGmaRow(index);
  }

  constructor() {
    effect(() => {
      const currentText = this.customerInfo();
      const isEditing = this.showRegionEditInfo();
      const formGroup = this.userService.regionFormGroup;

      if (!currentText) {
        this.contractFormService.patchEmptyRegionInfo();
      } else {
        this.contractFormService.patchRegionInfoFromText(
          currentText,
          isEditing
        );
      }

      if (isEditing) {
        formGroup.enable({ emitEvent: false });
      } else {
        formGroup.disable({ emitEvent: false });
      }
    });

    effect(() => {
      const currentText = this.customerContact();
      const isEditing = this.showContactEditInfo();
      const formGroup = this.userService.contactInfoFormGroup;

      if (!isEditing) {
        this.contractFormService.patchContactInfoFromText(currentText);
      }

      if (isEditing) {
        formGroup.enable({ emitEvent: false });
      } else {
        formGroup.disable({ emitEvent: false });
      }
    });
  }

  addContactRow(): void {
    this.contractFormService.addContactRow();
  }

  removeContactRow(index: number): void {
    this.contractFormService.removeContactRow(index);
  }

  onSaveContactInfo(): void {
    this.saveContactInfo.emit();
  }

  onEditRegion(): void {
    this.editRegion.emit();
  }

  onSaveRegionInfo(): void {
    const regionFormGroup = this.userService.regionFormGroup;

    if (regionFormGroup.invalid) {
      regionFormGroup.markAllAsTouched();
      regionFormGroup.updateValueAndValidity();

      return;
    }

    this.saveRegionInfo.emit(null);
  }

  onShowRegionEdit(show: boolean): void {
    this.showRegionEdit.emit(show);
  }

  onShowContactEdit(show: boolean): void {
    this.showContactEdit.emit(show);
  }
}
